import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Clock, Layers, GitCompare, Eye, AlertTriangle, Sun, Moon,
  ChevronDown, ChevronRight, Info, MapPin, User, Lock, X,
  Maximize2, Minimize2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * SCUTA — "Where time becomes visible"
 * 
 * BRAND LOCK (FINAL):
 * - Museum/archive/instrument tone
 * - Analytical, calm, time-aware, authoritative
 * - NOT a social app aesthetic
 * 
 * COLOR PALETTE (LOCKED):
 * - Background: #0B0F14 (Near-Black Blue)
 * - Primary Text: #E6E9EE (Archive White)
 * - Secondary Text: #9AA3AF (Muted Stone Gray)
 * - Accent: #C2A45E (Oxidized Gold)
 * - Dividers: #1E2936 (Slate Ink)
 */

export default function Scuta() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  
  // Theme - DEFAULT DARK (museum-grade, serious tone per directive)
  const [isDark, setIsDark] = useState(true);
  
  // Timelines
  const [timelines, setTimelines] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState(null);
  const [compareTimeline, setCompareTimeline] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [patterns, setPatterns] = useState(null);
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [density, setDensity] = useState('major'); // emergence, major, full
  const [viewMode, setViewMode] = useState('timeline'); // timeline, patterns, compare
  const [showDisclaimer, setShowDisclaimer] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState({});
  const [showSourcePath, setShowSourcePath] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [personalTimeline, setPersonalTimeline] = useState(null);

  // Force dark background on body for SCUTA pages
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#0B0F14';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);
  
  // Load timelines on mount
  useEffect(() => {
    loadTimelines();
    
    // Check URL params for pre-selected timeline
    const tid = searchParams.get('timeline');
    if (tid) setSelectedTimeline(tid);
  }, []);
  
  // Load timeline data when selection changes
  useEffect(() => {
    if (selectedTimeline) {
      loadTimelineData(selectedTimeline);
    }
  }, [selectedTimeline, density]);
  
  // Load compare data when compare timeline selected
  useEffect(() => {
    if (compareTimeline) {
      loadCompareData();
    }
  }, [compareTimeline, density]);
  
  const loadTimelines = async () => {
    try {
      const res = await axios.get(`${API}/api/scuta/timelines`);
      setTimelines(res.data.timelines || []);
      
      // Also try to load personal timeline if authenticated
      if (user) {
        try {
          const token = localStorage.getItem('token');
          const personalRes = await axios.get(`${API}/api/scuta/personal`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPersonalTimeline(personalRes.data);
        } catch { /* Silent */ }
      }
    } catch (error) {
      console.error('Failed to load timelines:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadTimelineData = async (timelineId) => {
    try {
      // Check if disclaimer required
      const timeline = timelines.find(t => t.id === timelineId);
      if (timeline?.requires_disclaimer && !disclaimerAccepted[timelineId]) {
        setShowDisclaimer(timelineId);
        return;
      }
      
      const res = await axios.get(`${API}/api/scuta/timelines/${timelineId}?density=${density}`);
      setTimelineData(res.data);
      
      // Also load patterns for patterns view
      if (viewMode === 'patterns') {
        const patternsRes = await axios.get(`${API}/api/scuta/patterns/${timelineId}`);
        setPatterns(patternsRes.data.patterns);
      }
    } catch (error) {
      toast.error('Failed to load timeline');
    }
  };
  
  const loadCompareData = async () => {
    if (!selectedTimeline || !compareTimeline) return;
    try {
      const res = await axios.get(`${API}/api/scuta/compare?timeline1=${selectedTimeline}&timeline2=${compareTimeline}&density=${density}`);
      setCompareData(res.data);
    } catch (error) {
      toast.error('Failed to load comparison');
    }
  };
  
  const acceptDisclaimer = (timelineId) => {
    setDisclaimerAccepted(prev => ({ ...prev, [timelineId]: true }));
    setShowDisclaimer(null);
    loadTimelineData(timelineId);
  };
  
  // Source colors
  const sourceColors = {
    academic: '#4A90A4',
    institutional: '#7B68EE',
    cultural: '#DDA15E',
    religious: '#9B2335'
  };
  
  // Format year for display
  const formatYear = (year) => {
    if (year < 0) return `${Math.abs(year)} BCE`;
    return `${year} CE`;
  };
  
  // Theme styles - SCUTA BRAND (LOCKED - True Black)
  // Dark mode: High contrast, museum-grade
  // Light mode: Limited use (schools/projection only)
  const theme = isDark ? {
    bg: '#000000',            // True black background (LOCKED)
    surface: '#0A0A0A',        // Subtle surface
    text: '#E6E9EE',           // Archive White (high contrast)
    textSecondary: '#B0B0B0',  // Secondary (readable)
    textMuted: '#808080',      // Muted
    border: '#1A1A1A',         // Subtle borders
    accent: '#C2A45E',         // Oxidized Gold
    gold: '#C2A45E'
  } : {
    bg: '#F4F6F8',             // Archival paper
    surface: '#FFFFFF',
    text: '#1A1F26',
    textSecondary: '#4B5563',
    textMuted: '#6B7280',
    border: '#E5E7EB',
    accent: '#C2A45E',
    gold: '#C2A45E'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}>
        <div className="text-center">
          <Clock className="w-12 h-12 mx-auto mb-4 animate-pulse" style={{ color: theme.accent }} />
          <p style={{ color: theme.text }}>Loading SCUTA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen fixed inset-0 overflow-auto" style={{ backgroundColor: theme.bg, color: theme.text }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6" style={{ color: theme.accent }} />
              <div>
                <h1 className="text-lg font-bold tracking-tight">SCUTA</h1>
                <p className="text-xs" style={{ color: theme.textSecondary }}>Where time becomes visible</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Light/Dark Toggle */}
              <button
                onClick={() => setIsDark(!isDark)}
                className="p-2 rounded-lg transition-colors"
                style={{ backgroundColor: isDark ? '#333' : '#eee' }}
                title={isDark ? 'Lights on (for schools)' : 'Lights off'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              {/* Questions Panel Toggle (Institutional) */}
              <button
                onClick={() => setShowQuestions(!showQuestions)}
                className="p-2 rounded-lg transition-colors"
                style={{ backgroundColor: showQuestions ? theme.accent : (isDark ? '#333' : '#eee'), color: showQuestions ? '#fff' : theme.text }}
                title="Teacher Questions"
              >
                <Info className="w-5 h-5" />
              </button>
              
              {/* Back to LikeKind */}
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
              >
                Back to LikeKind
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Timeline Selection */}
          <div className="w-64 flex-shrink-0">
            <Card className="p-4" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <h3 className="font-semibold mb-4" style={{ color: theme.text }}>Timelines</h3>
              
              {/* Personal Timeline (Pinned at Top) */}
              <div className="mb-4 pb-4 border-b" style={{ borderColor: theme.border }}>
                <button
                  onClick={() => {
                    if (personalTimeline?.has_access) {
                      setSelectedTimeline('personal');
                    } else {
                      toast.info('Available when signed in with LikeKind');
                    }
                  }}
                  className={`w-full text-left p-2 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedTimeline === 'personal' ? 'ring-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: selectedTimeline === 'personal' ? theme.accent + '20' : 'transparent',
                    ringColor: theme.accent,
                    opacity: personalTimeline?.has_access ? 1 : 0.6,
                    color: personalTimeline?.has_access ? theme.text : theme.textMuted
                  }}
                  data-testid="personal-timeline-btn"
                >
                  {personalTimeline?.has_access ? (
                    <User className="w-4 h-4" style={{ color: theme.accent }} />
                  ) : (
                    <Lock className="w-4 h-4" style={{ color: theme.textMuted }} />
                  )}
                  <span className="text-sm font-medium" style={{ color: personalTimeline?.has_access ? theme.text : theme.textMuted }}>Personal Timelines</span>
                </button>
                <p className="text-xs mt-1 px-2" style={{ color: theme.textMuted }}>
                  {personalTimeline?.has_access 
                    ? 'Private by default, shared by intention' 
                    : (user ? 'Requires Personal Member' : 'Sign in with LikeKind')}
                </p>
              </div>
              
              {/* All Timeline Categories - LOCKED ORDER (Tier-based) */}
              <div className="space-y-4">
                {/* TIER 1 - Must Feel Complete */}
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider mb-2 px-1" style={{ color: theme.accent }}>Core</p>
                  {timelines.filter(t => ['history', 'religion', 'conflict'].includes(t.category)).map(timeline => (
                    <TimelineButton key={timeline.id} timeline={timeline} theme={theme} selectedTimeline={selectedTimeline} setSelectedTimeline={setSelectedTimeline} />
                  ))}
                </div>
                
                {/* TIER 2 - High Value */}
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider mb-2 px-1" style={{ color: theme.accent }}>Expanded</p>
                  {timelines.filter(t => ['science', 'art', 'music'].includes(t.category)).map(timeline => (
                    <TimelineButton key={timeline.id} timeline={timeline} theme={theme} selectedTimeline={selectedTimeline} setSelectedTimeline={setSelectedTimeline} />
                  ))}
                </div>
                
                {/* TIER 3 - Growing Archives */}
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider mb-2 px-1" style={{ color: theme.accent }}>Growing</p>
                  {timelines.filter(t => ['agriculture', 'cultural', 'structure', 'unresolved'].includes(t.category)).map(timeline => (
                    <TimelineButton key={timeline.id} timeline={timeline} theme={theme} selectedTimeline={selectedTimeline} setSelectedTimeline={setSelectedTimeline} />
                  ))}
                  {timelines.filter(t => ['agriculture', 'cultural', 'structure', 'unresolved'].includes(t.category)).length === 0 && (
                    <p className="text-xs px-2" style={{ color: theme.textSecondary }}>This archive is growing.</p>
                  )}
                </div>
              </div>
              
              {/* Local History - Separate */}
              <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.border }}>
                <button
                  onClick={() => setSelectedTimeline('local')}
                  className="w-full text-left p-2 rounded-lg flex items-center gap-2 transition-colors"
                  style={{ 
                    backgroundColor: selectedTimeline === 'local' ? theme.accent + '20' : 'transparent',
                    color: theme.text
                  }}
                  data-testid="local-timeline-btn"
                >
                  <MapPin className="w-4 h-4" style={{ color: theme.accent }} />
                  <span className="text-sm" style={{ color: theme.text }}>Local / Regional History</span>
                </button>
                <p className="text-xs px-2 mt-1" style={{ color: theme.textSecondary }}>
                  Events specific to your location
                </p>
              </div>
            </Card>
            
            {/* Density Slider */}
            <Card className="p-4 mt-4" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <h4 className="font-medium mb-3 text-sm" style={{ color: theme.text }}>Density</h4>
              <div className="space-y-2">
                {[
                  { id: 'emergence', label: 'Emergence only' },
                  { id: 'major', label: 'Major entries' },
                  { id: 'full', label: 'Full density' }
                ].map(d => (
                  <button
                    key={d.id}
                    onClick={() => setDensity(d.id)}
                    className={`w-full text-left p-2 rounded-lg text-sm transition-colors ${
                      density === d.id ? 'font-medium' : ''
                    }`}
                    style={{ 
                      backgroundColor: density === d.id ? theme.accent + '20' : 'transparent',
                      color: density === d.id ? theme.accent : theme.text
                    }}
                    data-testid={`density-${d.id}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <p className="text-xs mt-3" style={{ color: theme.textSecondary }}>
                No importance ranking. Pure volume control.
              </p>
            </Card>
          </div>
          
          {/* Main Content */}
          <div className="flex-1">
            {/* View Mode Tabs */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2`}
                style={{ 
                  backgroundColor: viewMode === 'timeline' ? theme.accent : theme.surface,
                  color: viewMode === 'timeline' ? '#fff' : theme.text,
                  border: `1px solid ${theme.border}`
                }}
                data-testid="view-timeline"
              >
                <Clock className="w-4 h-4" /> Timeline
              </button>
              <button
                onClick={() => {
                  setViewMode('patterns');
                  if (selectedTimeline) loadTimelineData(selectedTimeline);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2`}
                style={{ 
                  backgroundColor: viewMode === 'patterns' ? theme.accent : theme.surface,
                  color: viewMode === 'patterns' ? '#fff' : theme.text,
                  border: `1px solid ${theme.border}`
                }}
                data-testid="view-patterns"
              >
                <Layers className="w-4 h-4" /> Patterns
              </button>
              <button
                onClick={() => setViewMode('compare')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2`}
                style={{ 
                  backgroundColor: viewMode === 'compare' ? theme.accent : theme.surface,
                  color: viewMode === 'compare' ? '#fff' : theme.text,
                  border: `1px solid ${theme.border}`
                }}
                data-testid="view-compare"
              >
                <GitCompare className="w-4 h-4" /> Compare
              </button>
              
              <div className="flex-1" />
              
              {/* Source Path Toggle */}
              <button
                onClick={() => setShowSourcePath(!showSourcePath)}
                className={`px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2`}
                style={{ 
                  backgroundColor: showSourcePath ? theme.accent + '20' : theme.surface,
                  color: showSourcePath ? theme.accent : theme.text,
                  border: `1px solid ${theme.border}`
                }}
                data-testid="toggle-sources"
              >
                <Eye className="w-4 h-4" /> Sources
              </button>
            </div>
            
            {/* Timeline View - HORIZONTAL LAYOUT */}
            {viewMode === 'timeline' && timelineData && (
              <Card className="p-6" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold">{timelineData.name}</h2>
                    <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>{timelineData.description}</p>
                    {timelineData.is_seed && (
                      <span className="inline-block mt-2 text-xs px-2 py-1 rounded" style={{ backgroundColor: theme.accent + '20', color: theme.accent }}>
                        Seed Data
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm" style={{ color: theme.textSecondary }}>
                      {timelineData.filtered_entries} of {timelineData.total_entries} entries
                    </p>
                  </div>
                </div>
                
                {/* HORIZONTAL Timeline */}
                <div className="overflow-x-auto pb-4">
                  <div className="relative min-w-max">
                    {/* Timeline Line */}
                    <div 
                      className="absolute top-6 left-0 right-0 h-1 rounded"
                      style={{ backgroundColor: theme.accent + '40' }}
                    />
                    
                    {/* Timeline Entries */}
                    <div className="flex gap-6 pt-0">
                      {timelineData.entries?.map((entry, i) => (
                        <div key={entry.id || i} className="relative flex flex-col items-center" style={{ minWidth: '180px', maxWidth: '220px' }}>
                          {/* Year marker */}
                          <div 
                            className="w-4 h-4 rounded-full border-2 z-10 mb-2"
                            style={{ backgroundColor: theme.surface, borderColor: theme.accent }}
                          />
                          
                          {/* Year */}
                          <span 
                            className="font-mono text-xs font-bold mb-2 px-2 py-1 rounded"
                            style={{ backgroundColor: theme.accent + '20', color: theme.accent }}
                          >
                            {formatYear(entry.year)}
                          </span>
                          
                          {/* Entry Card */}
                          <div 
                            className="p-3 rounded-lg text-center"
                            style={{ backgroundColor: theme.bg, border: `1px solid ${theme.border}` }}
                          >
                            <h3 className="font-semibold text-sm mb-1" style={{ color: theme.text }}>
                              {entry.title}
                            </h3>
                            <p className="text-xs" style={{ color: theme.textSecondary }}>
                              {entry.description.length > 80 ? entry.description.substring(0, 80) + '...' : entry.description}
                            </p>
                            
                            {/* Source Path */}
                            {showSourcePath && entry.sources && (
                              <div className="flex gap-1 mt-2 justify-center flex-wrap">
                                {entry.sources.map(source => (
                                  <span 
                                    key={source}
                                    className="text-xs px-2 py-0.5 rounded"
                                    style={{ backgroundColor: sourceColors[source] + '20', color: sourceColors[source] }}
                                  >
                                    {source}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-center mt-4" style={{ color: theme.textSecondary }}>
                  ← Scroll horizontally to explore the timeline →
                </p>
              </Card>
            )}
            
            {/* Patterns View */}
            {viewMode === 'patterns' && timelineData && (
              <Card className="p-6" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                <h2 className="text-xl font-bold mb-2">{timelineData.name} — Patterns</h2>
                <p className="text-sm mb-6" style={{ color: theme.textSecondary }}>
                  Visual patterns only. No explanations or conclusions.
                </p>
                
                {patterns ? (
                  <div className="space-y-8">
                    {/* Density Visualization */}
                    <div>
                      <h3 className="font-medium mb-3">Density by Century</h3>
                      <div className="flex items-end gap-1 h-32">
                        {Object.entries(patterns.density_by_century || {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(([century, count]) => (
                          <div key={century} className="flex-1 flex flex-col items-center">
                            <div 
                              className="w-full rounded-t"
                              style={{ 
                                height: `${(count / Math.max(...Object.values(patterns.density_by_century))) * 100}%`,
                                backgroundColor: theme.accent,
                                minHeight: '4px'
                              }}
                            />
                            <span className="text-xs mt-1 transform -rotate-45 origin-left" style={{ color: theme.textSecondary }}>
                              {Number(century) < 0 ? `${Math.abs(century)}BC` : century}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Clusters */}
                    {patterns.clustering?.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3">Clustering</h3>
                        <div className="flex flex-wrap gap-2">
                          {patterns.clustering.map((cluster, i) => (
                            <div 
                              key={i}
                              className="px-3 py-2 rounded-lg"
                              style={{ backgroundColor: theme.accent + '20' }}
                            >
                              <span className="text-sm font-mono">
                                {formatYear(cluster.start)} — {formatYear(cluster.end)}
                              </span>
                              <span className="text-xs ml-2" style={{ color: theme.textSecondary }}>
                                ({cluster.count} events)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Gaps */}
                    {patterns.gaps?.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3">Gaps</h3>
                        <div className="flex flex-wrap gap-2">
                          {patterns.gaps.map((gap, i) => (
                            <div 
                              key={i}
                              className="px-3 py-2 rounded-lg"
                              style={{ backgroundColor: '#DDA15E20' }}
                            >
                              <span className="text-sm font-mono">
                                {formatYear(gap.start)} — {formatYear(gap.end)}
                              </span>
                              <span className="text-xs ml-2" style={{ color: '#DDA15E' }}>
                                ({gap.duration} years)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ color: theme.textSecondary }}>Select a timeline to view patterns</p>
                )}
              </Card>
            )}
            
            {/* Compare View */}
            {viewMode === 'compare' && (
              <Card className="p-6" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                <h2 className="text-xl font-bold mb-4">Timeline Comparison</h2>
                <p className="text-sm mb-6" style={{ color: theme.textSecondary }}>
                  Compare up to 2 timelines. Events within 10 years are highlighted.
                </p>
                
                {/* Timeline Selection */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Timeline 1</label>
                    <select
                      value={selectedTimeline || ''}
                      onChange={(e) => setSelectedTimeline(e.target.value)}
                      className="w-full p-2 rounded-lg"
                      style={{ backgroundColor: theme.bg, color: theme.text, border: `1px solid ${theme.border}` }}
                    >
                      <option value="">Select timeline...</option>
                      {timelines.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Timeline 2</label>
                    <select
                      value={compareTimeline || ''}
                      onChange={(e) => setCompareTimeline(e.target.value)}
                      className="w-full p-2 rounded-lg"
                      style={{ backgroundColor: theme.bg, color: theme.text, border: `1px solid ${theme.border}` }}
                    >
                      <option value="">Select timeline...</option>
                      {timelines.filter(t => t.id !== selectedTimeline).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Parallel Timelines */}
                {compareData && (
                  <div className="space-y-4">
                    {/* Overlap indicators */}
                    {compareData.overlaps?.length > 0 && (
                      <div className="p-3 rounded-lg" style={{ backgroundColor: theme.accent + '10' }}>
                        <p className="text-sm font-medium" style={{ color: theme.accent }}>
                          {compareData.overlaps.length} potential connections found
                        </p>
                      </div>
                    )}
                    
                    {/* Side by side view */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Timeline 1 */}
                      <div className="border rounded-lg p-4" style={{ borderColor: theme.border }}>
                        <h3 className="font-semibold mb-4">{compareData.timeline1?.name}</h3>
                        <div className="space-y-3">
                          {compareData.timeline1?.entries?.slice(0, 10).map((entry, i) => {
                            const isOverlap = compareData.overlaps?.some(o => Math.abs(o.year1 - entry.year) <= 10);
                            return (
                              <div 
                                key={i}
                                className="p-2 rounded"
                                style={{ backgroundColor: isOverlap ? theme.accent + '20' : 'transparent' }}
                              >
                                <span className="font-mono text-xs" style={{ color: theme.accent }}>
                                  {formatYear(entry.year)}
                                </span>
                                <p className="text-sm font-medium">{entry.title}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Timeline 2 */}
                      <div className="border rounded-lg p-4" style={{ borderColor: theme.border }}>
                        <h3 className="font-semibold mb-4">{compareData.timeline2?.name}</h3>
                        <div className="space-y-3">
                          {compareData.timeline2?.entries?.slice(0, 10).map((entry, i) => {
                            const isOverlap = compareData.overlaps?.some(o => Math.abs(o.year2 - entry.year) <= 10);
                            return (
                              <div 
                                key={i}
                                className="p-2 rounded"
                                style={{ backgroundColor: isOverlap ? theme.accent + '20' : 'transparent' }}
                              >
                                <span className="font-mono text-xs" style={{ color: theme.accent }}>
                                  {formatYear(entry.year)}
                                </span>
                                <p className="text-sm font-medium">{entry.title}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            )}
            
            {/* No Timeline Selected */}
            {!timelineData && viewMode === 'timeline' && (
              <Card className="p-12 text-center" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                <Clock className="w-12 h-12 mx-auto mb-4" style={{ color: theme.textSecondary }} />
                <h2 className="text-xl font-bold mb-2">Select a Timeline</h2>
                <p style={{ color: theme.textSecondary }}>
                  Choose a timeline from the sidebar to explore history through time.
                </p>
              </Card>
            )}
          </div>
          
          {/* Questions Panel (Institutional) */}
          {showQuestions && (
            <div className="w-64 flex-shrink-0">
              <Card className="p-4" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-sm">Questions to Consider</h3>
                  <button onClick={() => setShowQuestions(false)}>
                    <X className="w-4 h-4" style={{ color: theme.textSecondary }} />
                  </button>
                </div>
                <div className="space-y-3">
                  {[
                    "What else is happening during this period?",
                    "What changes before this event?",
                    "Does this appear again later?",
                    "How does this compare to other regions?",
                    "What patterns do you notice?"
                  ].map((q, i) => (
                    <p key={i} className="text-sm p-2 rounded" style={{ backgroundColor: theme.bg }}>
                      {q}
                    </p>
                  ))}
                </div>
                <p className="text-xs mt-4" style={{ color: theme.textSecondary }}>
                  Questions to guide exploration. No answers provided.
                </p>
              </Card>
            </div>
          )}
        </div>
        
        {/* Source & Seed Data Note */}
        <div className="mt-8 text-center">
          <p className="text-xs" style={{ color: theme.textSecondary }}>
            SCUTA launches with curated starter data drawn from widely taught historical references.
            Seed data exists to demonstrate structure and comparison, not to represent a complete record of history.
          </p>
        </div>
      </div>
      
      {/* Disclaimer Modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <Card className="max-w-lg p-6" style={{ backgroundColor: theme.surface }}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: '#DDA15E' }} />
              <h3 className="font-bold text-lg">Content Notice</h3>
            </div>
            
            <p className="mb-6" style={{ color: theme.textSecondary }}>
              {timelines.find(t => t.id === showDisclaimer)?.id === 'conflict_history' 
                ? "This timeline documents wars, battles, and conflicts as they are recorded in history. It includes events involving violence, loss of life, or human suffering. SCUTA presents these records for historical understanding and context."
                : "This timeline documents historical claims, interpretations, or events that have been recorded, taught, or debated across different times, cultures, or institutions, and whose understanding remains interpreted differently. SCUTA presents these records for study and historical context."
              }
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDisclaimer(null)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => acceptDisclaimer(showDisclaimer)}
                className="flex-1"
                style={{ backgroundColor: theme.accent }}
              >
                I Understand
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Timeline Button Component for Sidebar
function TimelineButton({ timeline, theme, selectedTimeline, setSelectedTimeline }) {
  return (
    <button
      onClick={() => setSelectedTimeline(timeline.id)}
      className={`w-full text-left p-2 rounded-lg flex items-center justify-between transition-colors mb-1 ${
        selectedTimeline === timeline.id ? 'ring-1' : ''
      }`}
      style={{ 
        backgroundColor: selectedTimeline === timeline.id ? theme.accent + '20' : 'transparent',
        ringColor: theme.accent,
        color: theme.text
      }}
      data-testid={`timeline-${timeline.id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {timeline.requires_disclaimer && (
          <AlertTriangle className="w-3 h-3 flex-shrink-0" style={{ color: '#DDA15E' }} />
        )}
        <span className="text-sm truncate" style={{ color: theme.text }}>{timeline.name}</span>
      </div>
      <span className="text-xs ml-2 flex-shrink-0" style={{ color: theme.textSecondary }}>
        {timeline.entry_count || '–'}
      </span>
    </button>
  );
}
